import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import type { RateNegotiation } from "@src/appV2/redesign/Shift/Open/types";
import { PageHeaderWrapper, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { invalidateShifts } from "../../invalidateShifts";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { PullToRefresh } from "../ListView/PullToRefresh";
import { SHIFT_DISCOVERY_TRAVEL_FOR_MORE_SHIFTS_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { TravelForMoreShiftsList } from "./ShiftsList";
import { useTravelForMoreShifts } from "./useTravelForMoreShifts";

const EMPTY_RATE_NEGOTIATIONS_MAP: Map<string, RateNegotiation> = new Map();

interface ShiftDiscoveryTravelForMoreShiftsModalProps {
  modalState: UseModalState;
}

export function ShiftDiscoveryTravelForMoreShiftsModal(
  props: ShiftDiscoveryTravelForMoreShiftsModalProps
) {
  const { modalState } = props;
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { setBaseNavigationPathOverride } = useShiftModalsDataContext();

  useScrollToTopOnStatusTap(scrollRef);

  /**
   * Override the base navigation path while this modal is mounted so shift
   * modals opened from inside it stack on top, matching `FavoriteWorkplaces/Modal.tsx`.
   */
  useEffect(() => {
    setBaseNavigationPathOverride(SHIFT_DISCOVERY_TRAVEL_FOR_MORE_SHIFTS_PATH);

    return () => {
      setBaseNavigationPathOverride(undefined);
    };
  }, [setBaseNavigationPathOverride]);

  const { shifts, workplacesMap, isLoading, isSuccess, isError } = useTravelForMoreShifts({
    enabled: modalState.modalIsOpen,
  });

  const errorPayload = useMemo(
    () =>
      isError
        ? {
            error: new Error(
              "Travel for more shifts failed to load. Check RUM session for debugging."
            ),
          }
        : { error: undefined },
    [isError]
  );

  useLogErrorEffect(APP_V2_APP_EVENTS.GET_TRAVEL_FOR_MORE_SHIFTS_FAILURE, errorPayload, {
    enabled: isError,
  });

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <PageHeaderWrapper
        sx={{
          background: (theme) => theme.palette.background.tertiary,
        }}
      >
        <PageHeaderTopRow title="Travel for more shifts" />
      </PageHeaderWrapper>
      <PullToRefresh
        scrollRef={scrollRef}
        onRefresh={async () => {
          await invalidateShifts(queryClient);
        }}
      />
      <OpenShiftListDataProvider
        openShifts={shifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={EMPTY_RATE_NEGOTIATIONS_MAP}
        isOpenShiftsLoading={isLoading}
        isOpenShiftsError={isError}
        isOpenShiftsSuccess={isSuccess}
        canLoadMoreShifts={false}
        isLoadingMoreShifts={false}
      >
        <TravelForMoreShiftsList
          scrollRef={scrollRef}
          shifts={shifts}
          workplacesMap={workplacesMap}
        />
      </OpenShiftListDataProvider>
    </FullScreenDialog>
  );
}
